/* eslint-disable no-restricted-globals */
/**
 * Web Worker for client-side image background removal using @huggingface/transformers
 * Model: xrds/isnet-general-onnx-int8 (MIT License - Commercially Permitted)
 * Base Architecture: IS-Net / DIS (Dichotomous Image Segmentation for general objects, machinery, vehicles, and humans)
 */

export const BG_REMOVAL_MODEL_ID = 'xrds/isnet-general-onnx-int8';

export interface WorkerInferenceRequest {
  id: string;
  action: 'remove-background';
  blob: Blob;
  fileName: string;
}

export interface WorkerProgressMessage {
  id: string;
  type: 'progress';
  status: 'loading' | 'processing';
  message: string;
  progress?: number;
}

export interface WorkerSuccessMessage {
  id: string;
  type: 'success';
  blob: Blob;
  fileName: string;
}

export interface WorkerErrorMessage {
  id: string;
  type: 'error';
  error: string;
}

export type WorkerOutgoingMessage =
  | WorkerProgressMessage
  | WorkerSuccessMessage
  | WorkerErrorMessage;

interface TensorLike {
  dims?: number[];
  dispose?: () => void;
  mul: (val: number) => {
    to: (dtype: string) => unknown;
  };
  [index: number]: TensorLike;
}

interface LoadedPipeline {
  model: (inputs: { input: unknown }) => Promise<Record<string, TensorLike>>;
  processor: (image: unknown) => Promise<{ pixel_values: unknown }>;
  RawImage: {
    fromBlob: (source: Blob) => Promise<{
      width: number;
      height: number;
      resize: (width: number, height: number) => Promise<unknown>;
      putAlpha: (mask: unknown) => void;
      toBlob: (mimeType: string) => Promise<Blob>;
    }>;
    fromTensor: (tensor: unknown) => {
      resize: (width: number, height: number) => Promise<unknown>;
    };
  };
}

let pipelinePromise: Promise<LoadedPipeline> | null = null;

const getPipeline = async (requestId: string): Promise<LoadedPipeline> => {
  if (pipelinePromise) return pipelinePromise;

  pipelinePromise = (async () => {
    self.postMessage({
      id: requestId,
      type: 'progress',
      status: 'loading',
      message: 'Loading background removal model...',
    } as WorkerProgressMessage);

    const { AutoModel, AutoProcessor, RawImage, env } = await import('@huggingface/transformers');

    // Configure for browser worker execution
    env.allowLocalModels = false;
    if (env.backends?.onnx?.wasm) {
      env.backends.onnx.wasm.proxy = false;
      // Restrict WASM thread count to 1 inside the worker to prevent
      // thread pool exhaustion and renderer thread starvation
      env.backends.onnx.wasm.numThreads = 1;
    }

    const progressCallback = (info: { status?: string; progress?: number }) => {
      if (info.status === 'progress' && typeof info.progress === 'number') {
        self.postMessage({
          id: requestId,
          type: 'progress',
          status: 'loading',
          message: `Downloading model: ${Math.round(info.progress)}%`,
          progress: info.progress,
        } as WorkerProgressMessage);
      } else if (info.status === 'initiate' || info.status === 'download') {
        self.postMessage({
          id: requestId,
          type: 'progress',
          status: 'loading',
          message: 'Downloading background removal model...',
        } as WorkerProgressMessage);
      }
    };

    const [model, processor] = await Promise.all([
      AutoModel.from_pretrained(BG_REMOVAL_MODEL_ID, {
        progress_callback: progressCallback,
      }),
      AutoProcessor.from_pretrained(BG_REMOVAL_MODEL_ID, {}),
    ]);

    return {
      model: model as LoadedPipeline['model'],
      processor: processor as LoadedPipeline['processor'],
      RawImage: RawImage as LoadedPipeline['RawImage'],
    };
  })().catch((err) => {
    pipelinePromise = null;
    throw err;
  });

  return pipelinePromise;
};

self.onmessage = async (event: MessageEvent<WorkerInferenceRequest>) => {
  const { id, action, blob, fileName } = event.data;

  if (action !== 'remove-background') {
    self.postMessage({
      id,
      type: 'error',
      error: `Unknown action: ${String(action)}`,
    } as WorkerErrorMessage);
    return;
  }

  let rawImage: Awaited<ReturnType<LoadedPipeline['RawImage']['fromBlob']>> | null = null;
  let pixelValues: unknown = null;
  let modelResult: Record<string, TensorLike> | null = null;
  let outputTensor: TensorLike | null = null;

  try {
    const { model, processor, RawImage } = await getPipeline(id);

    self.postMessage({
      id,
      type: 'progress',
      status: 'processing',
      message: 'Segmenting foreground...',
    } as WorkerProgressMessage);

    rawImage = await RawImage.fromBlob(blob);
    const origWidth = rawImage.width;
    const origHeight = rawImage.height;

    // To prevent heavy bicubic preprocessing on massive multi-megapixel images,
    // downscale to 1024x1024 if needed for feature extraction, while keeping original
    // dimensions for final alpha compositing
    let inputForProcessor = rawImage;
    if (origWidth > 1024 || origHeight > 1024) {
      inputForProcessor = (await rawImage.resize(1024, 1024)) as typeof rawImage;
    }

    // Preprocess the input image
    const processed = await processor(inputForProcessor);
    pixelValues = processed.pixel_values;

    // Run inference to predict foreground segmentation mask
    modelResult = await model({ input: pixelValues });
    outputTensor = modelResult.output || modelResult.output0 || modelResult[Object.keys(modelResult)[0]];
    const maskSlice = outputTensor.dims && outputTensor.dims.length === 4 ? outputTensor[0] : outputTensor;

    // Dispose unused intermediate model outputs immediately to free WebAssembly heap memory
    for (const key of Object.keys(modelResult)) {
      const tensor = modelResult[key];
      if (tensor !== outputTensor && typeof tensor?.dispose === 'function') {
        try {
          tensor.dispose();
        } catch {
          // ignore
        }
      }
    }

    if (pixelValues && typeof (pixelValues as { dispose?: () => void }).dispose === 'function') {
      try {
        (pixelValues as { dispose: () => void }).dispose();
      } catch {
        // ignore
      }
    }

    self.postMessage({
      id,
      type: 'progress',
      status: 'processing',
      message: 'Encoding transparent PNG...',
    } as WorkerProgressMessage);

    // Resize segmentation matte back to original dimensions
    const scaledMaskTensor = maskSlice.mul(255).to('uint8');
    const mask = await RawImage
      .fromTensor(scaledMaskTensor)
      .resize(origWidth, origHeight);

    // Dispose output tensor once mask has been extracted
    if (typeof outputTensor?.dispose === 'function') {
      try {
        outputTensor.dispose();
      } catch {
        // ignore
      }
    }

    // Apply alpha mask directly in-place without redundant cloning
    rawImage.putAlpha(mask);

    const resultBlob = await rawImage.toBlob('image/png');
    const baseName = fileName.replace(/\.[^/.]+$/, '');
    const transparentFileName = `${baseName}-transparent.png`;

    self.postMessage({
      id,
      type: 'success',
      blob: resultBlob,
      fileName: transparentFileName,
    } as WorkerSuccessMessage);
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    self.postMessage({
      id,
      type: 'error',
      error: errorMessage || 'Failed to remove background',
    } as WorkerErrorMessage);
  } finally {
    // Explicitly release references
    rawImage = null;
    pixelValues = null;
    modelResult = null;
    outputTensor = null;
  }
};

export {};
